import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, TextField, Typography, } from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useTheme } from '@mui/material/styles';
import { useAuth } from 'oidc-react';
import { AlertMessage } from '@eposnow/ui-core';
import { ReactComponent as IconFeedback } from '../../img/IconFeedback.svg';
import { ReactComponent as SuccessStar0 } from '../../img/Success-Star-0.svg';
import { ReactComponent as SuccessStar1 } from '../../img/Success-Star_blue.svg';
import { ReactComponent as SuccessStarDm0 } from '../../img/Success-Star-dm-0.svg';
import { ReactComponent as SuccessStarDm1 } from '../../img/Success-Star-dm-1.svg';
import { sendDashboardsFeedback } from '../../api/dashboards';
import { UserContext } from '../../context/UserContext';
import { useBrowserInfo } from '../../helpers/useBrowserInfo';



export interface FeedbackDialogProps {
    dialogOpen: boolean;
    description: string;
    featureKey: string;
    onClose: () => void;
    onSuccess: () => void;
    inputText?: string;
}

const FeedbackDialog = (props: FeedbackDialogProps) => {
    const auth = useAuth();
    const { locale } = useContext(UserContext);
    const maybeAccessToken = auth?.userData?.access_token;
    const { description, dialogOpen, featureKey, onClose, onSuccess, inputText } = props;
    const { t } = useTranslation();
    const [startFeedbackOpen, setStartFeedbackOpen] = useState(false);
    const [clickedStartIndex, setClickedStartIndex] = useState(-1);
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { browser, deviceType } = useBrowserInfo();
    const [comments, setComments] = useState("");
    const [failedCall, setFailedCall] = useState(0);



    const handleClose = () => {
        onClose();
        setStartFeedbackOpen(false);
        setClickedStartIndex(-1);
        setComments("");
        setFailedCall(0);
    }

    const sendFeedback = () => {
        sendDashboardsFeedback(
            {
                featureKey,
                featureVersion: "v1.0",
                rating: clickedStartIndex + 1,
                comment: comments,
                browser,
                deviceType
            },
            maybeAccessToken)
            .then(() => {
                handleClose()
                onSuccess()
            })
            .catch(() => setFailedCall((prev) => prev + 1));
    }


    const handleStarClick = (index: number) => {
        setClickedStartIndex(prev => {
            const newValue = prev === index ? -1 : index;
            setStartFeedbackOpen(newValue !== -1);
            return newValue;
        });
    };

    const getStarIcon = (selected: boolean) => {
        if (isDarkMode) {
            return selected ? SuccessStarDm1 : SuccessStarDm0;
        }
        return selected ? SuccessStar1 : SuccessStar0;
    };


    return (
        <Dialog
            open={dialogOpen}
            onClose={handleClose}
            aria-labelledby="feedback-dialog-title"
            aria-describedby="feedback-dialog-description"
            PaperProps={{ sx: { padding: '24px', width: '500px' } }}
        >
            <IconButton
                sx={{
                    position: 'absolute',
                    top: '20px',
                    marginLeft: '470px',
                }} onClick={handleClose}>
                <CloseOutlinedIcon />
            </IconButton>
            <DialogTitle id="feedback-dialog-title" sx={{ alignItems: 'center', marginTop: '25px', fontWeight: 600, gap: 1.5, display: 'flex', flexDirection: 'column', }}>
                <IconFeedback />
                {t('actions.feedback')}
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
                <DialogContentText id="feedback-dialog-description">
                    {description}
                    <Box sx={{ marginTop: "20px" }}>
                        <Box display="flex" justifyContent="center" gap={2}>
                            {[1, 2, 3, 4, 5].map((value, index) => {
                                const isSelected = clickedStartIndex === index;
                                const IconComponent = getStarIcon(isSelected);

                                return (
                                    <Box
                                        key={value}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                    >
                                        <IconButton onClick={() => handleStarClick(index)}
                                            sx={{
                                                transition: 'transform 0.2s ease',
                                                transform: isSelected ? 'scale(1.25)' : 'scale(1)',
                                                '& svg': {
                                                    width: isSelected ? 36 : 30,
                                                    height: isSelected ? 36 : 30,
                                                    transition: 'width 0.2s ease, height 0.2s ease',
                                                },
                                            }}>
                                            <IconComponent />
                                        </IconButton>

                                        <Typography variant="caption" sx={{ color: isSelected ? 'primary.main' : 'default' }}>
                                            {value}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    {startFeedbackOpen &&
                        <Box>
                            <Box sx={{ marginTop: '25px' }} >
                                <TextField sx={{ width: '450px' }}
                                    id="outlined-basic"
                                    label={inputText}
                                    variant="outlined"
                                    multiline
                                    minRows={4}
                                    maxRows={8}
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                />
                            </Box>
                            {failedCall === 1 &&
                                <Box sx={{ marginTop: '40px' }}>
                                    <AlertMessage type='error' locale={locale} theme={theme} styles={{ marginBottom: 2, marginTop: '-23px', marginLeft: '-23px', marginRight: '-23px' }} iconAlignment='center'>
                                        {t('actions.feedbackFaildCall')}
                                    </AlertMessage>
                                </Box>
                            }
                            {failedCall >= 2 &&
                                <Box sx={{ marginTop: '40px' }}>
                                    <AlertMessage type='error' locale={locale} theme={theme} styles={{ marginBottom: 2, marginTop: '-23px', marginLeft: '-23px', marginRight: '-23px' }} iconAlignment='center'>
                                        {t('actions.feedbackFailedSendDescription')}
                                    </AlertMessage>
                                </Box>
                            }
                        </Box>
                    }
                </DialogContentText>
            </DialogContent>
            {startFeedbackOpen &&
                <DialogActions>
                    {failedCall < 2 &&
                        <Button
                            size="large"
                            data-testid="dialog-discard"
                            onClick={() => {
                                handleClose();
                            }}
                        >
                            {t('actions.cancel')}
                        </Button>}
                    {failedCall >= 2 &&
                        <Button
                            size="large"
                            data-testid="dialog-discard"
                            onClick={() => sendFeedback()}
                        >
                            {t('actions.resendFeedback')}
                        </Button>}
                    {failedCall === 0 &&
                        <Button
                            size="large"
                            color="primary"
                            variant="contained"
                            data-testid="dialog-send"
                            onClick={() => sendFeedback()}
                            autoFocus
                        >
                            {t('actions.sendFeedback')}
                        </Button>}
                    {failedCall === 1 &&
                        <Button
                            size="large"
                            color="primary"
                            variant="contained"
                            data-testid="dialog-send"
                            onClick={() => sendFeedback()}
                            autoFocus
                        >
                            {t('actions.resendFeedback')}
                        </Button>
                    }
                    {failedCall >= 2 &&
                        <Button
                            size="large"
                            color="primary"
                            variant="contained"
                            data-testid="dialog-send"
                            onClick={() => {
                                handleClose();
                            }}
                            autoFocus
                        >
                            {t('actions.gotIt')}
                        </Button>
                    }
                </DialogActions>
            }
        </Dialog>


    );
};

export default FeedbackDialog;
